import React, { useState } from "react";
import { 
  ChartBarIcon, 
  GlobeAmericasIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  UserGroupIcon,
  CogIcon
} from "@heroicons/react/24/outline";
import type { ProjectInsightsProps } from "./projectInsights.types";

export default function ProjectInsights({ insights, type = "description" }: ProjectInsightsProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!insights) return null;

  const getSectionIcon = (section: string) => {
    switch (section) {
      case 'businessValue': return ChartBarIcon;
      case 'marketContext': return GlobeAmericasIcon;
      case 'riskFactors': return ExclamationTriangleIcon;
      case 'successMetrics': return CheckCircleIcon;
      case 'keyFeatures': return CogIcon;
      case 'targetUsers': return UserGroupIcon;
      default: return CheckCircleIcon;
    }
  };

  const getSectionColor = (section: string) => {
    switch (section) {
      case 'businessValue': return 'text-green-600 bg-green-50 border-green-200';
      case 'marketContext': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'riskFactors': return 'text-red-600 bg-red-50 border-red-200';
      case 'successMetrics': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'keyFeatures': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'targetUsers': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSectionTitle = (section: string) => {
    switch (section) {
      case 'businessValue': return 'Valor de Negócio';
      case 'marketContext': return 'Contexto de Mercado';
      case 'riskFactors': return 'Fatores de Risco';
      case 'successMetrics': return 'Métricas de Sucesso';
      case 'keyFeatures': return 'Funcionalidades Principais';
      case 'targetUsers': return 'Usuários Alvo';
      case 'technicalConsiderations': return 'Considerações Técnicas';
      default: return section;
    }
  };

  const sectionsToShow = Object.entries(insights).filter(([_, value]) => 
    value && (Array.isArray(value) ? value.length > 0 : value.toString().trim().length > 0)
  );

  if (sectionsToShow.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center space-x-2 mb-4">
        <ChartBarIcon className="h-5 w-5 text-indigo-600" />
        <h3 className="text-md font-medium text-gray-900">
          {type === 'description' ? 'Insights do Projeto' : 'Insights Técnicos'}
        </h3>
        <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">
          Gerado por IA
        </span>
      </div>

      <div className="space-y-3">
        {sectionsToShow.map(([section, value]) => {
          const Icon = getSectionIcon(section);
          const colorClasses = getSectionColor(section);
          const isExpanded = expandedSections[section];
          const isArray = Array.isArray(value);

          return (
            <div key={section} className={`rounded-lg border p-3 ${colorClasses}`}>
              <button
                onClick={() => toggleSection(section)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center space-x-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {getSectionTitle(section)}
                  </span>
                  {isArray && (
                    <span className="text-xs bg-white bg-opacity-70 px-2 py-0.5 rounded-full">
                      {(value as any[]).length}
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-white border-opacity-50">
                  {isArray ? (
                    <ul className="space-y-2">
                      {(value as string[]).map((item, index) => (
                        <li key={index} className="text-sm flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 bg-current rounded-full mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm leading-relaxed">
                      {value as string}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}